import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, InputNumber, Select, Button, Space, Spin, Alert, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getTenant,
  createTenant,
  updateTenant,
  type CreateTenantRequest,
  type UpdateTenantRequest,
} from '@/api/adminApi';
import { useAuth } from '@/hooks/useAuth';

const PLANS = ['free', 'starter', 'professional', 'enterprise'];
const STATUSES = ['active', 'suspended', 'cancelled'];

interface TenantFormValues {
  name: string;
  domain: string;
  plan: string;
  status?: string;
  logo_url?: string;
  locale?: string;
  contact_email?: string;
  contact_phone?: string;
  max_users?: number;
  address?: string;
  notes?: string;
}

export default function TenantForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm<TenantFormValues>();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    setError(null);
    getTenant(id)
      .then((tenant) => {
        form.setFieldsValue({
          name: tenant.name,
          domain: tenant.domain,
          plan: tenant.plan,
          status: tenant.status,
          logo_url: tenant.logo_url,
          locale: tenant.locale,
          contact_email: tenant.contact_email,
          contact_phone: tenant.contact_phone,
          max_users: tenant.max_users,
          address: tenant.address,
          notes: tenant.notes,
        });
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [isEdit, id, form, t]);

  const handleSubmit = async (values: TenantFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && id) {
        const dto: UpdateTenantRequest = {
          name: values.name,
          domain: values.domain,
          plan: values.plan,
          status: values.status,
          logo_url: values.logo_url,
          locale: values.locale,
          contact_email: values.contact_email,
          contact_phone: values.contact_phone,
          max_users: values.max_users,
          address: values.address,
          notes: values.notes,
        };
        await updateTenant(id, dto);
        message.success(t('admin.tenant_update_success'));
      } else {
        const dto: CreateTenantRequest = {
          name: values.name,
          domain: values.domain,
          plan: values.plan,
          locale: values.locale,
          contact_email: values.contact_email,
          contact_phone: values.contact_phone,
          max_users: values.max_users,
          address: values.address,
          notes: values.notes,
        };
        await createTenant(dto);
        message.success(t('admin.tenant_create_success'));
      }
      navigate('/admin/tenants');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('app.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasPermission('admin', 'write')) {
    return <Alert type="error" message={t('app.error')} />;
  }

  if (loading) return <Spin size="large" />;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/tenants')}>
          {t('app.back')}
        </Button>
      </Space>

      <h2>{isEdit ? t('admin.edit_tenant_title') : t('admin.create_tenant_title')}</h2>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ plan: 'free', max_users: 10 }}
        style={{ maxWidth: 600 }}
      >
        <Form.Item
          name="name"
          label={t('admin.field.name')}
          rules={[{ required: true, message: t('admin.validation.name_required') }]}
        >
          <Input placeholder={t('admin.field.name')} />
        </Form.Item>

        <Form.Item
          name="domain"
          label={t('admin.field.domain')}
          rules={[{ required: true, message: t('admin.validation.domain_required') }]}
        >
          <Input placeholder={t('admin.field.domain')} />
        </Form.Item>

        <Form.Item name="plan" label={t('admin.field.plan')}>
          <Select>
            {PLANS.map((p) => (
              <Select.Option key={p} value={p}>
                {t(`admin.plan_options.${p}`)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {isEdit && (
          <Form.Item name="status" label={t('admin.field.status')}>
            <Select>
              {STATUSES.map((s) => (
                <Select.Option key={s} value={s}>
                  {t(`admin.status_options.${s}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item name="locale" label={t('admin.field.locale')}>
          <Select allowClear placeholder={t('admin.field.locale')}>
            <Select.Option value="zh-TW">繁體中文</Select.Option>
            <Select.Option value="zh-CN">简体中文</Select.Option>
            <Select.Option value="en-US">English</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="max_users"
          label={t('admin.field.max_users')}
          rules={[{ type: 'number', min: 1, message: t('admin.validation.max_users_min') }]}
        >
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item name="contact_email" label={t('admin.field.contact_email')}>
          <Input type="email" placeholder={t('admin.field.contact_email')} />
        </Form.Item>

        <Form.Item name="contact_phone" label={t('admin.field.contact_phone')}>
          <Input placeholder={t('admin.field.contact_phone')} />
        </Form.Item>

        <Form.Item name="address" label={t('admin.field.address')}>
          <Input.TextArea rows={2} placeholder={t('admin.field.address')} />
        </Form.Item>

        {isEdit && (
          <Form.Item name="logo_url" label={t('admin.field.logo_url')}>
            <Input placeholder={t('admin.field.logo_url')} />
          </Form.Item>
        )}

        <Form.Item name="notes" label={t('admin.field.notes')}>
          <Input.TextArea rows={3} placeholder={t('admin.field.notes')} />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('app.save')}
            </Button>
            <Button onClick={() => navigate('/admin/tenants')}>{t('app.cancel')}</Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
